import { createClient, createConfig } from '../openapi-ts/client/index.js';
// @transform-path ../../../package.json
import pkg from "../../../package.json" with { type: 'json' };
import { HttpError } from '../errors/HttpError.js';
import { ApiError } from '../errors/ApiError.js';
import * as sdk from '../openapi-ts/sdk.gen.js';
import { MeasurementStatus, } from '../openapi-ts/index.js';
import { wait } from '../utils.js';
export class Globalping {
    auth;
    client;
    userAgent;
    throwApiErrors;
    timeout;
    constructor({ auth, userAgent, throwApiErrors, timeout } = {}) {
        this.userAgent = userAgent ?? `globalping-typescript/${pkg.version} (https://github.com/jsdelivr/globalping-typescript)`;
        this.throwApiErrors = throwApiErrors ?? false;
        this.timeout = timeout ?? 30000;
        this.auth = auth;
        this.client = createClient(createConfig({
            baseUrl: 'https://api.globalping.io',
            fetch: (...args) => {
                const request = new Request(...args);
                const headers = new Headers(request.headers);
                headers.set('User-Agent', this.userAgent);
                if (this.auth) {
                    headers.set('Authorization', `Bearer ${this.auth}`);
                }
                return fetch(new Request(request, {
                    headers,
                    signal: AbortSignal.any([request.signal, AbortSignal.timeout(this.timeout)]),
                }));
            },
        }));
    }
    /**
     * @see {@link sdk.createMeasurement} for the API docs
     */
    createMeasurement(measurement) {
        return this.transformResult(sdk.createMeasurement({
            body: measurement,
            client: this.client,
        }));
    }
    /**
     * @see {@link sdk.getMeasurement} for the API docs
     */
    async getMeasurement(id) {
        const result = await this.transformResult(sdk.getMeasurement({
            path: { id },
            client: this.client,
        }));
        if (!result.ok) {
            return result;
        }
        return result;
    }
    /**
     * @see {@link sdk.getMeasurement} for the API docs
     */
    async awaitMeasurement(id, { signal } = {}) {
        const getMeasurement = (eTag) => {
            return sdk.getMeasurement({
                path: { id },
                headers: eTag ? {
                    'If-None-Match': eTag,
                } : {},
                ...signal ? { signal } : {},
                client: this.client,
            });
        };
        const start = Date.now();
        let internalResult = Globalping.requireRequestCompleted(await getMeasurement());
        const maxTime = internalResult.data?.timeout === undefined ? 35000 : (internalResult.data.timeout + 10) * 1000;
        while (internalResult.data && internalResult.data.status === MeasurementStatus.IN_PROGRESS) {
            if (Date.now() - start > maxTime) {
                throw new Error(`Timed out waiting for measurement ${id} to finish.`);
            }
            await wait(500, signal);
            const newInternalResult = Globalping.requireRequestCompleted(await getMeasurement(internalResult.response.headers.get('ETag')));
            if (newInternalResult.response.status !== 304) {
                internalResult = newInternalResult;
            }
        }
        const result = await this.transformResult(internalResult);
        if (!result.ok) {
            return result;
        }
        const data = result.data;
        Globalping.assertMeasurementFinished(data);
        return { ...result, data };
    }
    /**
     * @see {@link sdk.listProbes} for the API docs
     */
    async listProbes() {
        return this.transformResult(sdk.listProbes({
            client: this.client,
        }));
    }
    /**
     * @see {@link sdk.getLimits} for the API docs
     */
    async getLimits() {
        return this.transformResult(sdk.getLimits({
            client: this.client,
        }));
    }
    static requireRequestCompleted(result) {
        if (!result.request || !result.response) {
            if ('error' in result) {
                throw result.error;
            }
            throw new Error('Request failed before receiving a response.');
        }
        return result;
    }
    async transformResult(requestResult) {
        const internalResult = Globalping.requireRequestCompleted(await requestResult);
        const { data, error, request, response } = internalResult;
        if (error != null) {
            if (response.ok) {
                if (error instanceof Error) {
                    throw error;
                }
                const responseError = new Error('Failed to process response.');
                Object.defineProperty(responseError, 'cause', { value: error });
                throw responseError;
            }
            if (typeof error !== 'object' || !('error' in error)) {
                throw new HttpError(request, response);
            }
            if (this.throwApiErrors) {
                throw new ApiError(request, response, error);
            }
            return { ok: false, data: error, request, response };
        }
        if (data == null) {
            throw new Error('Unexpected undefined data');
        }
        return { ok: true, data, request, response };
    }
    static assertHttpStatus(status, result) {
        if (!Globalping.isHttpStatus(status, result)) {
            throw new Error(`Expected HTTP status ${status}, got ${result.response.status}`);
        }
    }
    static assertMeasurementFinished(measurement) {
        if (!Globalping.isMeasurementFinished(measurement)) {
            throw new Error(`Expected measurement status ${MeasurementStatus.FINISHED}, got ${measurement.status}`);
        }
    }
    static assertMeasurementType(type, measurement) {
        if (!Globalping.isMeasurementType(type, measurement)) {
            throw new Error(`Expected measurement type ${type}, got ${measurement.type}`);
        }
    }
    static isHttpStatus(status, result) {
        return result.response.status === status;
    }
    static isMeasurementFinished(measurement) {
        return measurement.status === MeasurementStatus.FINISHED;
    }
    static isMeasurementType(type, measurement) {
        return measurement.type === type;
    }
}
export default Globalping;
//# sourceMappingURL=Globalping.js.map